import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { analytics } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Copy, Check, Plus, Trash2, AlertTriangle, Flame, ExternalLink, RotateCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useUsage, type UsageStatus, type UsageMeter } from '@/hooks/useUsage';
import { useShortcuts } from '@/hooks/useShortcuts';
import { KeyboardChord } from '@/pages/onboarding/components/KeyboardChord';
import { CopyShortcutsLink } from '@/components/CopyShortcutsLink';
import type { ShortcutInfo } from '@/types/extension';

// ────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────

interface ApiKey {
  id: string;
  key_prefix: string;
  name: string | null;
  created_at: string;
  last_used_at: string | null;
}

interface BillingStatus {
  plan: 'saver' | 'pro' | 'founding';
  is_pro: boolean;
  trial: { ends_at: string; days_left: number } | null;
  subscription: {
    id: string;
    status: string;
    tier: 'pro_monthly' | 'pro_yearly' | 'founding_yearly' | null;
    interval: 'month' | 'year' | null;
    price_id: string;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    canceled_at: string | null;
  } | null;
  was_founding: boolean;
  had_trial: boolean;
  has_stripe_customer: boolean;
  founding_seats: { claimed: number; cap: number };
}

// ────────────────────────────────────────────────────────────────────
// Fetch helpers
// ────────────────────────────────────────────────────────────────────

async function getAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload.error ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

interface PendingCheckoutSnapshot {
  upgrade_intent_id?: string;
  plan?: string;
  interval?: 'month' | 'year';
  from?: string;
  stripe_session_id?: string;
  ts?: number;
}

function readPendingCheckout(): PendingCheckoutSnapshot | null {
  try {
    const raw = window.localStorage.getItem('solem_pending_checkout');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckoutSnapshot;
    if (parsed?.ts && Date.now() - parsed.ts > 30 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearPendingCheckout(): void {
  try { window.localStorage.removeItem('solem_pending_checkout'); } catch { /* no-op */ }
}

// ────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────

const CLAUDE_DESKTOP_SNIPPET = `{
  "mcpServers": {
    "solem": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "https://mcp.solem.ai/mcp",
        "--header",
        "Authorization: Bearer YOUR_API_KEY"
      ]
    }
  }
}`;

const CLAUDE_CODE_SNIPPET = `claude mcp add solem -- npx -y mcp-remote https://mcp.solem.ai/mcp --header "Authorization: Bearer YOUR_API_KEY"`;

const OPENCLAW_SNIPPET = `{
  "mcpServers": {
    "solem": {
      "url": "https://mcp.solem.ai/mcp",
      "headers": { "Authorization": "Bearer YOUR_API_KEY" }
    }
  }
}`;

const SECTIONS = [
  { id: 'profile', label: 'Profile' },
  { id: 'plan', label: 'Plan' },
  { id: 'billing', label: 'Billing' },
  { id: 'api-keys', label: 'API keys' },
  { id: 'agents', label: 'AI agents' },
  { id: 'shortcuts', label: 'Keyboard shortcuts' },
  { id: 'account', label: 'Account' },
] as const;

const CHROME_WEB_STORE_URL =
  import.meta.env.VITE_CHROME_WEB_STORE_URL ||
  'https://chromewebstore.google.com/';

type SectionId = typeof SECTIONS[number]['id'];

// ────────────────────────────────────────────────────────────────────
// Small UI bits
// ────────────────────────────────────────────────────────────────────

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-neutral-950 text-neutral-100 rounded-lg p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all">
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded bg-neutral-800 text-neutral-300 hover:bg-neutral-700 transition-colors opacity-0 group-hover:opacity-100"
        title="Copy"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function PlanBadge({ plan, isTrialing }: { plan: BillingStatus['plan']; isTrialing: boolean }) {
  if (plan === 'founding') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-[#FF3100]/10 text-[#FF3100] ring-1 ring-[#FF3100]/20">
        <Flame size={11} fill="currentColor" />
        Founding
      </span>
    );
  }
  if (plan === 'pro') {
    if (isTrialing) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-200">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-600" />
          </span>
          Pro · Trial
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-600 text-white">
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-300" />
        Pro · Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200">
      Saver
    </span>
  );
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function tierLabel(tier: BillingStatus['subscription'] extends infer S ? (S extends { tier: infer T } ? T : never) : never): string {
  if (tier === 'pro_monthly') return 'Pro · Monthly';
  if (tier === 'pro_yearly') return 'Pro · Yearly';
  if (tier === 'founding_yearly') return 'Founding · Yearly';
  return 'Pro';
}

// ────────────────────────────────────────────────────────────────────
// Section: Profile
// ────────────────────────────────────────────────────────────────────

function ProfileSection({ user, onSignOut }: { user: { email?: string; user_metadata?: { full_name?: string; avatar_url?: string } } | null; onSignOut: () => void }) {
  const name = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Solem user';
  const avatar = user?.user_metadata?.avatar_url;

  return (
    <SectionShell id="profile" title="Profile" description="Your account details.">
      <div className="flex items-center gap-4">
        {avatar ? (
          <img src={avatar} alt="" className="h-12 w-12 rounded-full" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-500 text-lg font-medium">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{name}</p>
          <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={onSignOut}>Sign out</Button>
      </div>
    </SectionShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Section: Plan
// ────────────────────────────────────────────────────────────────────

function UsageBar({ label, meter }: { label: string; meter: UsageMeter }) {
  const limit = meter.limit;
  const used = meter.used;
  const pct = limit == null || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const display = limit == null ? `${used} / ∞` : `${used} / ${limit}`;
  const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-neutral-700';

  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-neutral-600">{label}</span>
        <span className="font-mono tabular-nums text-neutral-900">{display}</span>
      </div>
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-neutral-100">
        {limit != null && (
          <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
        )}
      </div>
    </div>
  );
}

function UsageMeters({ usage }: { usage: UsageStatus }) {
  return (
    <div className="rounded-sm border border-neutral-200 bg-neutral-50/60 p-4 space-y-3">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
        This month
      </p>
      <UsageBar label="Saved pages" meter={usage.saves} />
      <UsageBar label="Chat messages" meter={usage.ai_message} />
      <UsageBar label="MCP calls" meter={usage.mcp_call} />
    </div>
  );
}

function PlanSection({ status, loading, onUpgrade }: { status: BillingStatus | null; loading: boolean; onUpgrade: () => void }) {
  const { usage } = useUsage(!!status);
  if (loading || !status) {
    return (
      <SectionShell id="plan" title="Plan" description="Your subscription and trial status.">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </SectionShell>
    );
  }

  const isTrialing = !!status.trial;
  const sub = status.subscription;

  // Primary line + secondary detail vary by state. Compute them in one place.
  let primary: string;
  let secondary: string | null = null;

  if (isTrialing) {
    const days = status.trial!.days_left;
    primary = `Trial ends in ${days} day${days !== 1 ? 's' : ''}`;
    secondary = `Drops to Saver on ${formatDate(status.trial!.ends_at)} unless you subscribe.`;
  } else if (sub) {
    primary = tierLabel(sub.tier);
    secondary = sub.cancel_at_period_end
      ? `Ends on ${formatDate(sub.current_period_end)}`
      : `Renews on ${formatDate(sub.current_period_end)}`;
  } else {
    primary = 'Saver — free forever';
    secondary = '100 saves · 20 chats / month · 10 MCP calls / month';
  }

  return (
    <SectionShell id="plan" title="Plan" description="Your subscription and trial status.">
      <div className="space-y-5">
        {/* Primary row: status text on left, badge on right */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-neutral-900">{primary}</p>
            {secondary && (
              <p className="text-sm text-muted-foreground mt-0.5">{secondary}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <PlanBadge plan={status.plan} isTrialing={isTrialing} />
            {status.was_founding && status.plan !== 'founding' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#FF3100]/5 text-[#FF3100] ring-1 ring-[#FF3100]/20">
                <Flame size={9} fill="currentColor" /> Former founding
              </span>
            )}
          </div>
        </div>

        {/* State warnings */}
        {sub && sub.cancel_at_period_end && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
            <p className="font-medium text-amber-900">
              Cancels on {formatDate(sub.current_period_end)}
            </p>
            <p className="text-amber-800">Click "Manage billing" below to resume.</p>
          </div>
        )}

        {sub && sub.status === 'past_due' && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm">
            <p className="font-medium text-red-900 flex items-center gap-1.5">
              <AlertTriangle size={14} /> Payment failed
            </p>
            <p className="text-red-800">Update your card via "Manage billing" to keep Pro access.</p>
          </div>
        )}

        {usage && <UsageMeters usage={usage} />}

        <div className="pt-1">
          <Button
            onClick={onUpgrade}
            variant={status.is_pro && sub ? 'outline' : 'default'}
            className="rounded-sm"
          >
            {status.is_pro && sub ? 'Change plan' : status.is_pro ? 'Subscribe now' : 'Upgrade to Pro'}
          </Button>
        </div>
      </div>
    </SectionShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Section: Billing
// ────────────────────────────────────────────────────────────────────

function BillingSection({ status, onManageBilling, portalLoading }: { status: BillingStatus | null; onManageBilling: () => void; portalLoading: boolean }) {
  const hasCustomer = !!status?.has_stripe_customer;

  return (
    <SectionShell id="billing" title="Billing" description="Manage payment method, invoices, and cancellation.">
      <div className="space-y-4">
        {hasCustomer ? (
          <div className="space-y-3">
            <Button variant="outline" className="rounded-sm" onClick={onManageBilling} disabled={portalLoading}>
              <ExternalLink className="h-3.5 w-3.5 mr-2" />
              {portalLoading ? 'Opening…' : 'Manage billing'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Update card, download invoices, or cancel — all handled by Stripe.
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No billing account yet. Subscribe via the Plan section above to enable invoices and payment management.
          </p>
        )}

        <div className="border-t pt-4 text-sm space-y-1">
          <p className="font-medium">Need a refund?</p>
          <p className="text-muted-foreground">
            We honor a 30-day no-questions-asked refund. Email{' '}
            <a
              className="text-blue-600 hover:underline"
              href="mailto:support@solem.ai?subject=Refund%20request"
            >
              support@solem.ai
            </a>{' '}
            and we'll process it within one business day.
          </p>
        </div>

        <div className="text-sm space-y-1">
          <p className="font-medium">Need to switch plans?</p>
          <p className="text-muted-foreground">
            Use "Change plan" in the Plan section — Pro monthly ↔ yearly switches with prorated billing.
          </p>
        </div>
      </div>
    </SectionShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Section: API keys (credential CRUD)
// ────────────────────────────────────────────────────────────────────

function ApiKeysSection({
  keys,
  keysLoading,
  onCreateClick,
  onRevokeClick,
}: {
  keys: ApiKey[];
  keysLoading: boolean;
  onCreateClick: () => void;
  onRevokeClick: (k: ApiKey) => void;
}) {
  return (
    <SectionShell
      id="api-keys"
      title="API keys"
      description="Personal tokens your AI agents use to access your Solem corpus. Create one per agent so you can revoke individually."
    >
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Your keys</h3>
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm"
            onClick={onCreateClick}
            disabled={keys.length >= 5}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create key
          </Button>
        </div>
        {keysLoading ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-md">
            No API keys yet. Create one to connect your first AI agent.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.id}>
                  <TableCell className="font-medium">{k.name ?? <span className="text-muted-foreground italic">Unnamed</span>}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded">
                      {k.key_prefix}…
                    </code>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(k.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {k.last_used_at ? formatDistanceToNow(new Date(k.last_used_at), { addSuffix: true }) : '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive hover:bg-red-50"
                      onClick={() => onRevokeClick(k)}
                      title="Revoke key"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {keys.length >= 5 && (
          <p className="text-xs text-muted-foreground mt-3">
            Maximum of 5 active keys reached. Revoke an existing key to create a new one.
          </p>
        )}
      </div>
    </SectionShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Section: AI agents (how to connect Claude Desktop / Code / OpenClaw)
// ────────────────────────────────────────────────────────────────────

function AgentsSection({ hasKeys }: { hasKeys: boolean }) {
  return (
    <SectionShell
      id="agents"
      title="AI agents"
      description="Plug Solem into your AI agent over MCP. Pick the config that matches your client and paste in an API key from above."
    >
      {!hasKeys && (
        <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
          <p className="text-amber-900">
            Create an API key first — the snippets below use it as the auth header.
          </p>
        </div>
      )}

      <div className="space-y-5">
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Claude Desktop / Cursor
          </h4>
          <p className="text-xs text-muted-foreground mb-2">
            Add to <code>claude_desktop_config.json</code> or Cursor MCP settings:
          </p>
          <CodeBlock code={CLAUDE_DESKTOP_SNIPPET} />
        </div>
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Claude Code
          </h4>
          <p className="text-xs text-muted-foreground mb-2">Run in your terminal:</p>
          <CodeBlock code={CLAUDE_CODE_SNIPPET} />
        </div>
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            OpenClaw
          </h4>
          <CodeBlock code={OPENCLAW_SNIPPET} />
        </div>
      </div>
    </SectionShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Section: Keyboard shortcuts (defensive recovery surface)
// ────────────────────────────────────────────────────────────────────
//
// Why this exists: Chrome silently drops `suggested_key` bindings on
// extension update / conflict / user edit. A long-tenured user whose
// binding breaks has no in-app way to recover — the onboarding wizard's
// fix-it copy only fires during onboarding. This card is the
// post-onboarding recovery surface.
//
// State machine (driven by useShortcuts):
//   loading        → 2-row skeleton (don't flash "not installed")
//   not_installed  → install CTA + Chrome Web Store link
//   installed_old  → update CTA + CopyShortcutsLink fallback
//   error          → soft error + CopyShortcutsLink fallback
//   supported      → list of bound/unbound rows + global "Fix in Chrome"
//
// Brand: blue-600 accent, rounded-sm, emerald-500 small dot for bound,
// amber-700 for unbound. The CopyShortcutsLink sits adjacent to the
// primary "Fix in Chrome" button so the affordance never dead-ends.

/**
 * Map a single Chrome modifier-name token to the glyph we want to render.
 * Platform-aware: `Alt` is the Mac Option key when running on macOS, but
 * literally the Alt key on Windows/Linux — render accordingly so e.g.
 * "Alt+Period" on Windows doesn't masquerade as a Mac chord.
 *
 * Token reference (Chrome commands API):
 *   - `Command`, `Cmd`        Mac only          → ⌘
 *   - `MacCtrl`               Mac only          → ⌃
 *   - `Option`                Mac only          → ⌥
 *   - `Ctrl`, `Control`       Cross-platform    → Ctrl (or ⌃ on Mac in some
 *                                                  user-customised setups —
 *                                                  but Chrome normalises to
 *                                                  `MacCtrl` for that case,
 *                                                  so `Ctrl` stays "Ctrl")
 *   - `Alt`                   Mac → ⌥ ; else → Alt
 *   - `Shift`                 Always            → ⇧
 *   - Everything else (letters, digits, `Period`, `Space`, etc.) is left
 *     verbatim so it shows up as a single-key chip.
 */
function mapKeyToken(token: string, isMac: boolean): string {
  const t = token.trim();
  switch (t) {
    case 'Command':
    case 'Cmd':
      return '⌘';
    case 'MacCtrl':
      return '⌃';
    case 'Option':
      return '⌥';
    case 'Alt':
      return isMac ? '⌥' : 'Alt';
    case 'Ctrl':
    case 'Control':
      return 'Ctrl';
    case 'Shift':
      return '⇧';
    default:
      return t;
  }
}

/**
 * Parse Chrome's plus-delimited shortcut string into chip labels.
 *
 * Examples:
 *   Mac:     "Command+Shift+E"  →  ["⌘", "⇧", "E"]
 *   Windows: "Ctrl+Shift+E"     →  ["Ctrl", "⇧", "E"]
 *   Windows: "Alt+Period"       →  ["Alt", "Period"]
 *   Mac:     "Alt+Period"       →  ["⌥", "Period"]
 *   ""                          →  []
 *
 * Defensive: if the input is something weird (no `+`), return it as a
 * single chip rather than dropping it — better to show the raw string
 * than break the UI on unexpected Chrome output.
 */
function parseShortcutString(s: string, isMac: boolean): string[] {
  if (!s) return [];
  if (!s.includes('+')) return [s];
  return s.split('+').map((tok) => mapKeyToken(tok, isMac));
}

function ShortcutRow({ info, isMac }: { info: ShortcutInfo; isMac: boolean }) {
  const isBound = info.shortcut.length > 0;
  const chipKeys = parseShortcutString(info.shortcut, isMac);

  return (
    <div className="flex items-center justify-between gap-4 rounded-sm border border-neutral-200 bg-white px-4 py-3">
      <div className="flex min-w-0 items-center gap-4">
        {isBound ? (
          <KeyboardChord keys={chipKeys} size="md" />
        ) : (
          <span className="inline-flex h-9 items-center rounded-sm border border-amber-300 bg-amber-50 px-3 text-xs font-medium text-amber-800">
            Not set
          </span>
        )}
        <p className="truncate text-sm text-neutral-700">{info.description || info.name}</p>
      </div>
      <div className="shrink-0">
        {isBound ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Bound
          </span>
        ) : (
          <span className="text-xs font-medium text-amber-700">Needs fixing</span>
        )}
      </div>
    </div>
  );
}

function ShortcutsLoading() {
  return (
    <div className="space-y-2">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-4 rounded-sm border border-neutral-200 bg-white px-4 py-3"
        >
          <div className="flex items-center gap-4">
            <div className="h-9 w-32 animate-pulse rounded-sm bg-neutral-100" />
            <div className="h-3 w-40 animate-pulse rounded-sm bg-neutral-100" />
          </div>
          <div className="h-3 w-12 animate-pulse rounded-sm bg-neutral-100" />
        </div>
      ))}
    </div>
  );
}

function ShortcutsSection() {
  const { state, refetch, openShortcutsPage } = useShortcuts();
  const [opening, setOpening] = useState(false);
  const { toast } = useToast();
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);

  const handleFixInChrome = useCallback(async () => {
    setOpening(true);
    try {
      const opened = await openShortcutsPage();
      if (!opened) {
        // Couldn't open via the extension. CopyShortcutsLink is visible
        // adjacent to this button as the always-available fallback, so
        // just nudge the user toward it.
        toast({
          title: "Couldn't open Chrome shortcuts page",
          description: 'Copy the URL beside this button and paste it into a new tab.',
        });
      }
    } finally {
      setOpening(false);
    }
  }, [openShortcutsPage, toast]);

  return (
    <SectionShell
      id="shortcuts"
      title="Keyboard shortcuts"
      description="Your Solem extension's save bindings. Chrome occasionally drops these on update — this is where you fix them."
    >
      {state.kind === 'loading' && <ShortcutsLoading />}

      {state.kind === 'not_installed' && (
        <div className="rounded-sm border border-neutral-200 bg-white p-4">
          <p className="text-sm font-medium text-neutral-900">Install the Solem extension</p>
          <p className="mt-1.5 text-sm text-neutral-600">
            The shortcut bindings live in the Chrome extension. Install it to see them here.
          </p>
          <Button asChild size="sm" className="mt-4 rounded-sm">
            <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
              Install from Chrome Web Store
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      )}

      {state.kind === 'installed_old' && (
        <div className="space-y-3">
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-900">Update the Solem extension</p>
            <p className="mt-1.5 text-sm text-amber-800">
              Your version doesn't expose its shortcut bindings here yet. Update from the Chrome
              Web Store — or open the shortcuts page directly using the link below.
            </p>
            <Button asChild size="sm" variant="outline" className="mt-3 rounded-sm border-amber-300 bg-white text-amber-900 hover:bg-amber-50">
              <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
                Update from Chrome Web Store
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500">Or paste this into a new tab:</span>
            <CopyShortcutsLink variant="quiet" />
          </div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="space-y-3">
          <div className="rounded-sm border border-neutral-200 bg-white p-4">
            <p className="text-sm font-medium text-neutral-900">Couldn't read your bindings</p>
            <p className="mt-1.5 text-sm text-neutral-600">
              The extension is installed but didn't respond. Try refreshing this section, or open
              Chrome's shortcuts page directly with the link below.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 rounded-sm"
              onClick={refetch}
            >
              <RotateCw className="mr-2 h-3.5 w-3.5" /> Try again
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500">Or paste this into a new tab:</span>
            <CopyShortcutsLink variant="quiet" />
          </div>
        </div>
      )}

      {state.kind === 'supported' && (
        <div className="space-y-4">
          {state.shortcuts.length === 0 ? (
            <p className="text-sm text-neutral-600">
              The extension didn't report any commands. This is unusual — try refreshing.
            </p>
          ) : (
            <div className="space-y-2">
              {state.shortcuts.map((info) => (
                <ShortcutRow key={info.name} info={info} isMac={isMac} />
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              size="sm"
              className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
              onClick={handleFixInChrome}
              disabled={opening}
            >
              {opening ? 'Opening…' : 'Fix in Chrome'}
              {!opening && <ExternalLink className="ml-2 h-3.5 w-3.5" />}
            </Button>
            <span className="text-xs text-neutral-400">— or —</span>
            <CopyShortcutsLink variant="quiet" />
          </div>

          <p className="text-xs text-neutral-500">
            Chrome's shortcuts page opens in a new tab. We'll refresh this section when you come
            back.
          </p>
        </div>
      )}
    </SectionShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Section: Account (delete account)
// ────────────────────────────────────────────────────────────────────

function AccountSection({ onDeleteClick, onReplayClick }: { onDeleteClick: () => void; onReplayClick: () => void }) {
  return (
    <SectionShell id="account" title="Account" description="Manage your onboarding and account.">
      <div className="space-y-4">
        <div className="rounded-sm border border-neutral-200 bg-white p-4">
          <h3 className="font-medium text-neutral-900">Replay onboarding</h3>
          <p className="text-sm text-neutral-600 mt-1.5">
            Walk through the setup again. Useful after switching machines.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4 rounded-sm"
            onClick={onReplayClick}
          >
            Restart onboarding
          </Button>
        </div>
        <div className="rounded-md border border-red-200 bg-red-50/30 p-4">
          <h3 className="font-medium text-red-900 flex items-center gap-2">
            <AlertTriangle size={16} /> Delete account
          </h3>
          <p className="text-sm text-red-800 mt-1.5">
            Permanently delete your account, all saved pages, chat history, API keys, and cancel any active subscription. This cannot be undone.
          </p>
          <Button
            variant="destructive"
            size="sm"
            className="mt-4 rounded-sm"
            onClick={onDeleteClick}
          >
            Delete account
          </Button>
        </div>
      </div>
    </SectionShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Shared section shell
// ────────────────────────────────────────────────────────────────────

function SectionShell({ id, title, description, children }: { id: SectionId; title: string; description?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <Card className="rounded-sm border-neutral-200 bg-neutral-50 shadow-none">
        <CardHeader className="space-y-0.5">
          <CardTitle className="text-xl font-light tracking-tight text-neutral-900">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sticky left nav
// ────────────────────────────────────────────────────────────────────

function SideNav({ active }: { active: SectionId }) {
  return (
    <nav className="hidden lg:block sticky top-24 self-start">
      <ul className="space-y-0.5 text-sm">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className={`block px-3 py-2 rounded-sm transition-colors ${
                active === s.id
                  ? 'bg-neutral-100 text-neutral-900 font-medium'
                  : 'text-muted-foreground hover:bg-neutral-50 hover:text-neutral-900'
              }`}
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────

export function Settings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Billing status
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  // API keys
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Delete account
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Active section for sticky nav
  const [activeSection, setActiveSection] = useState<SectionId>('profile');
  const lastScrollSync = useRef<number>(0);

  // ─────────── Data loading ───────────

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiFetch<BillingStatus>('/api/billing/status');
      setStatus(data);
    } catch (err) {
      // Stale JWT for a deleted account → server returns 410 / profile_missing.
      // Sign the user out so they land on the home page cleanly. Also clear
      // any stored pending destination — otherwise we'd bounce right back
      // here on the next sign-in and loop forever.
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('Profile no longer exists') || msg.includes('profile_missing')) {
        try { sessionStorage.removeItem('solem_pending_destination'); } catch { /* ignore */ }
        await supabase.auth.signOut();
        navigate('/');
        return;
      }
      toast({ title: 'Failed to load plan status', description: msg, variant: 'destructive' });
    } finally {
      setStatusLoading(false);
    }
  }, [toast, navigate]);

  const fetchKeys = useCallback(async () => {
    try {
      const data = await apiFetch<ApiKey[]>('/api/keys');
      setKeys(data);
    } catch (err) {
      toast({ title: 'Failed to load API keys', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setKeysLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
      return;
    }
    if (!loading && user) {
      fetchStatus();
      fetchKeys();
    }
  }, [user, loading, navigate, fetchStatus, fetchKeys]);

  // ─────────── Post-checkout refresh ───────────

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('billing') === 'success') {
      const sessionId = params.get('session_id');
      const pending = readPendingCheckout();
      analytics.track('checkout_success_returned', {
        upgrade_intent_id: pending?.upgrade_intent_id,
        plan: pending?.plan,
        interval: pending?.interval,
        stripe_session_id: sessionId || pending?.stripe_session_id,
        from: pending?.from,
      });
      clearPendingCheckout();
      // Strip query so a refresh doesn't re-trigger
      navigate('/settings', { replace: true });

      // Self-heal: webhook may not have fired yet (e.g. local dev without
      // `stripe listen`). Force-sync from the session_id before refetching.
      (async () => {
        if (sessionId) {
          try {
            await apiFetch('/api/billing/sync-session', {
              method: 'POST',
              body: JSON.stringify({ session_id: sessionId }),
            });
          } catch (err) {
            // Non-fatal — webhook will eventually catch up. Just log.
            // eslint-disable-next-line no-console
            console.warn('sync-session failed (webhook may catch up later):', err);
          }
        }
        toast({ title: 'Subscription active', description: 'Welcome to Solem Pro.' });
        fetchStatus();
      })();
    } else if (params.get('billing') === 'canceled') {
      const pending = readPendingCheckout();
      analytics.track('checkout_abandoned', {
        upgrade_intent_id: pending?.upgrade_intent_id,
        plan: pending?.plan,
        time_on_page_seconds: pending?.ts ? Math.round((Date.now() - pending.ts) / 1000) : undefined,
      });
      clearPendingCheckout();
      toast({ title: 'Checkout canceled', description: 'No charge made.' });
      navigate('/settings', { replace: true });
    }
  }, [location.search, toast, navigate, fetchStatus]);

  // ─────────── Portal return detection ───────────
  // The Stripe Customer Portal sends users back to /settings with no query
  // param marker, so we use a localStorage timestamp written immediately
  // before window.location = portalUrl as our return signal.
  useEffect(() => {
    if (loading || !user) return;
    const raw = (() => {
      try { return window.localStorage.getItem('solem_portal_open_at'); } catch { return null; }
    })();
    if (!raw) return;
    const openAt = Number(raw);
    if (!openAt || Number.isNaN(openAt)) return;
    const seconds = Math.round((Date.now() - openAt) / 1000);
    try { window.localStorage.removeItem('solem_portal_open_at'); } catch { /* no-op */ }
    if (seconds < 0 || seconds > 30 * 60) return; // stale — ignore
    analytics.track('portal_returned', {
      time_in_portal_seconds: seconds,
      // outcome derived dashboard-side by joining with subsequent
      // subscription_* server events on user_id+timestamp.
    });
  }, [loading, user]);

  // ─────────── Hash sync + scrollspy ───────────

  useEffect(() => {
    const hash = location.hash.replace('#', '') as SectionId;
    if (SECTIONS.some((s) => s.id === hash)) {
      setActiveSection(hash);
      // Defer until DOM mounted
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      // throttle
      const now = Date.now();
      if (now - lastScrollSync.current < 100) return;
      lastScrollSync.current = now;

      // Find the section whose top is closest to (but not past) the trigger line
      const triggerY = 120;
      let current: SectionId = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= triggerY) current = s.id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ─────────── Handlers ───────────

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleUpgradeClick = () => {
    const id = analytics.beginUpgradeIntent('settings_plan', {
      current_plan: status?.plan,
      current_interval: status?.subscription?.interval ?? null,
      destination_path: '/upgrade',
    });
    navigate(`/upgrade?upgrade_intent_id=${id}&from=settings_plan`);
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { url } = await apiFetch<{ url: string }>('/api/billing/portal', { method: 'POST' });
      // Stash open timestamp so the return-detector effect can fire
      // portal_returned with a real time_in_portal_seconds.
      try { window.localStorage.setItem('solem_portal_open_at', String(Date.now())); } catch { /* no-op */ }
      window.location.href = url;
    } catch (err) {
      toast({ title: 'Could not open billing portal', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
      setPortalLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const data = await apiFetch<{ key: string; prefix: string; name: string | null; id: string; created_at: string }>('/api/keys', {
        method: 'POST',
        body: JSON.stringify({ name: newKeyName.trim() || undefined }),
      });
      setCreatedKey(data.key);
      setKeys((prev) => [{ id: data.id, key_prefix: data.prefix, name: data.name, created_at: data.created_at, last_used_at: null }, ...prev]);
    } catch (err) {
      toast({ title: 'Failed to create API key', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
      setCreateOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const handleCopyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
    setCreatedKey(null);
    setNewKeyName('');
    setKeyCopied(false);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await apiFetch(`/api/keys/${revokeTarget.id}`, { method: 'DELETE' });
      setKeys((prev) => prev.filter((k) => k.id !== revokeTarget.id));
      toast({ title: 'API key revoked' });
    } catch (err) {
      toast({ title: 'Failed to revoke key', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);

    // Client-side intent event fires BEFORE the API call so we capture
    // even if the server delete fails. The server's account_deleted (S3.6)
    // is the canonical funnel signal; this one tracks the click intent.
    analytics.track('account_delete_initiated', {
      surface: 'settings',
    });

    try {
      const res = await apiFetch<{ ok?: boolean; redirect_to?: string }>(
        '/api/account',
        { method: 'DELETE' }
      );
      analytics.track('account_delete_confirmed', {
        surface: 'settings',
        had_redirect: !!res?.redirect_to,
      });

      await supabase.auth.signOut();

      // posthog-js is configured with capture_pageleave: true and uses the
      // sendBeacon API on unload, so a hard navigation here flushes any
      // queued events. window.location.replace triggers that path.
      // Hard-replace to the marketing site with UTM (S3.6 contract) so the
      // user lands cleanly on solem.ai instead of the in-app /. Fall back
      // to / if the server didn't return a redirect (older deploy).
      const target = res?.redirect_to || '/';
      window.location.replace(target);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      analytics.track('account_delete_failed', {
        surface: 'settings',
        error_message: msg.slice(0, 200),
      });
      toast({ title: 'Failed to delete account', description: msg, variant: 'destructive' });
      setDeleting(false);
    }
  };

  const handleReplayOnboarding = async () => {
    try {
      await apiFetch('/api/onboarding/replay', { method: 'POST' });
      window.location.assign('/onboarding');
    } catch (err) {
      toast({
        title: 'Could not restart onboarding',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  // ─────────── Render ───────────

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        user={user}
        onSearch={(q) => navigate(`/search?q=${encodeURIComponent(q)}`)}
        onSignOut={handleSignOut}
        isLoading={loading}
      />

      <main className="container max-w-screen-xl 2xl:max-w-[1600px] mx-auto px-4 py-8">
        <div className="mb-4">
          <h1 className="text-2xl font-light tracking-tight text-neutral-900">Settings</h1>
        </div>

        <div className="relative">
          {/* Sidebar pinned to the left of the main container, spans full height
              so the inner sticky nav can scroll-stick. */}
          <aside className="hidden lg:block absolute top-0 left-0 w-40 h-full">
            <SideNav active={activeSection} />
          </aside>

          {/* Cards centered in the viewport, independent of sidebar offset. */}
          <div className="max-w-[720px] mx-auto space-y-8 min-w-0">
            <ProfileSection user={user} onSignOut={handleSignOut} />
            <PlanSection status={status} loading={statusLoading} onUpgrade={handleUpgradeClick} />
            <BillingSection status={status} onManageBilling={handleManageBilling} portalLoading={portalLoading} />
            <ApiKeysSection
              keys={keys}
              keysLoading={keysLoading}
              onCreateClick={() => setCreateOpen(true)}
              onRevokeClick={setRevokeTarget}
            />
            <AgentsSection hasKeys={keys.length > 0} />
            <ShortcutsSection />
            <AccountSection
              onDeleteClick={() => setDeleteOpen(true)}
              onReplayClick={handleReplayOnboarding}
            />
          </div>
        </div>
      </main>

      {/* Create Key Dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) handleCreateClose(); else setCreateOpen(true); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Give your key a name so you can identify it later.
            </DialogDescription>
          </DialogHeader>

          {!createdKey ? (
            <>
              <Input
                placeholder="e.g. Claude Desktop, Cursor"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !creating) handleCreate(); }}
                maxLength={100}
              />
              <DialogFooter>
                <Button variant="outline" onClick={handleCreateClose}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating…' : 'Create'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  This key will only be shown once. Copy it now.
                </p>
                <div className="relative">
                  <code className="block bg-neutral-950 text-neutral-100 rounded-lg px-4 py-3 text-xs break-all pr-10">
                    {createdKey}
                  </code>
                  <button
                    onClick={handleCopyKey}
                    className="absolute top-2 right-2 p-1.5 rounded bg-neutral-800 text-neutral-300 hover:bg-neutral-700 transition-colors"
                    title="Copy key"
                  >
                    {keyCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreateClose}>Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently revoke{' '}
              <strong>{revokeTarget?.name ?? revokeTarget?.key_prefix}</strong>. Any agent using this key will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} disabled={revoking} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {revoking ? 'Revoking…' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Confirmation */}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteOpen(false);
            setDeleteConfirm('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete account?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">This will:</span>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Permanently delete all saved pages and chat history</li>
                <li>Revoke all API keys</li>
                <li>Cancel your subscription immediately (no refund — request separately via support if eligible)</li>
                <li>Sign you out</li>
              </ul>
              <span className="block pt-2 font-medium text-red-700">
                Type <code className="bg-red-50 px-1 py-0.5 rounded">DELETE</code> below to confirm.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            spellCheck={false}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting || deleteConfirm !== 'DELETE'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete forever'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
